<template>
  <v-card class="py-8 px-6 text-center mx-auto ma-4" elevation="12" max-width="400" width="100%">
    <h3 class="text-h6 mb-4">
      {{ t("devices.assign.title") }}
    </h3>
    <div v-if="!assignSuccess">
      <div class="text-body-2">
        {{ t("devices.assign.description") }}
      </div>
      <v-alert v-if="submitError" class="mt-6 mb-6" type="error">
        {{ submitError }}
      </v-alert>
      <assign-device-onboarding-alert v-if="!canAssignDevice" />
      <SiteSelector v-model="selectedSite" :disabled="loadingData" />
      <v-otp-input v-model="model.otp" :disabled="loadingData" autocomplete="off" required />
      <v-btn
        :loading="loadingData"
        :disabled="v$.$invalid || !canAssignDevice"
        @click="assignDevice"
      >
        {{ t("devices.assign.connectButton") }}
      </v-btn>
    </div>
    <div v-else>
      <v-alert type="info" class="text-sm-left mb-6">
        {{ t("devices.assign.successMessage") }}
      </v-alert>
      <v-btn @click="router.push(`/devices?selectedSite=${selectedSite}`)">
        {{ t("devices.assign.backButton") }}
      </v-btn>
    </div>
    <v-card-subtitle class="mt-6 text-left pl-0">
      {{ t("devices.assign.helpText") }}
      <a href="https://help.yetipay.me/en/articles/10221550-connecting-my-device" target="_blank">{{
        t("devices.assign.helpLink")
      }}</a>
    </v-card-subtitle>
  </v-card>
</template>

<script setup lang="ts">
import useVuelidate from "@vuelidate/core";
import { integer, maxLength, minLength, required } from "@vuelidate/validators";
import { computed, inject, onMounted, ref, Ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import { AssignDevice } from "@/api/merchant-service/device";
import { API } from "@/plugins/api";
import router from "@/router";
import { useMerchantsStore } from "@/store/merchants";
import { requiredField } from "@/utils/custom-validators";

const { t } = useI18n();
const api = inject<API>("api");
const route = useRoute();

const { currentMerchant } = useMerchantsStore();
const selectedSite = ref<string | undefined>(undefined);
const loadingData = ref(false);
const submitError = ref<string | undefined>(undefined);
const assignSuccess = ref(false);

onMounted(() => {
  if (route.query.selectedSite) {
    selectedSite.value = route.query.selectedSite as string;
  }
});

const canAssignDevice = computed(
  () =>
    (currentMerchant?.onboardingActions?.pciAgreement === "DONE" &&
      currentMerchant?.onboardingActions?.serviceAgreement === "DONE") ||
    currentMerchant?.kycOnboardingStatus === "verified",
);

const model = ref<AssignDevice & { selectedSite: Ref<string | undefined> }>({
  otp: "",
  selectedSite,
});

const v$ = useVuelidate(
  {
    otp: {
      $autoDirty: true,
      requiredField,
      minLength: minLength(6),
      maxLength: maxLength(6),
      integer,
    },
    selectedSite: {
      $autoDirty: true,
      required,
    },
  },
  model,
);

const assignDevice = async () => {
  if (!(await v$.value.$validate())) {
    return;
  }
  try {
    loadingData.value = true;
    await api?.device.assignDevice(selectedSite.value!, {
      otp: model.value.otp,
    });
    assignSuccess.value = true;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.error(error);
    if (error.response?.status === 404) {
      submitError.value = t("devices.assign.errors.deviceNotFound");
      return;
    }
    const message = error.response?.data?.error;
    submitError.value = message ?? t("devices.assign.errors.genericError");
    return;
  } finally {
    loadingData.value = false;
  }
};
</script>
